/**
 * Categories the API can report. The API returns a key rather than a URL, so routing, labels and
 * icons stay owned by the client — see OUTSTANDING_ACTION_ROUTES.
 */
export const OUTSTANDING_ACTION_KEYS = [
  'accessToGrant',
  'accessToRemove',
  'outstandingCustomerFeedback',
  'membershipRequests',
] as const

export type OutstandingActionKey = (typeof OUTSTANDING_ACTION_KEYS)[number]

export type OutstandingActionSeverity = 'info' | 'warning' | 'danger'

export interface OutstandingAction {
  key: OutstandingActionKey
  count: number
  oldestItemAt: string | null
  oldestItemAgeHours: number | null
  severity: OutstandingActionSeverity
}

export interface GetOutstandingActionsResponse {
  generatedAt: string
  /** Across every returned category — the number the header badge shows. */
  totalCount: number
  actions: OutstandingAction[]
}

export const isKnownOutstandingActionKey = (key: string): key is OutstandingActionKey =>
  (OUTSTANDING_ACTION_KEYS as readonly string[]).includes(key)

/**
 * Why a digest run did or did not send. Recorded on every evaluation, not just the ones that sent,
 * so a quiet inbox can be told apart from a scheduler that has stopped.
 */
export type DigestOutcome =
  | 'NothingOutstanding'
  | 'NothingNew'
  | 'SentNewItems'
  | 'SentEscalated'

export interface OutstandingActionsDigestRun {
  ranAt: string
  outcome: DigestOutcome
  sent: boolean
  /** What was awaiting action when it ran, whether or not an email went out. */
  outstandingCount: number
  /** 'Scheduled' or 'Manual'. */
  trigger: string
  /** How many items were reported, keyed by category. Empty when nothing was sent. */
  reportedCounts: Record<string, number>
}

export interface GetLastOutstandingActionsDigestRunResponse {
  lastRun: OutstandingActionsDigestRun | null
}

export interface GetOutstandingActionsDigestRunsResponse {
  runs: OutstandingActionsDigestRun[]
}
